import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams, useNavigate } from 'react-router-dom'
import { hpcStore } from '../stores/HPCStore'
import Card from '../components/common/Card'
import Button from '../components/common/Button'
import Badge from '../components/common/Badge'
import { HPCJobStatus } from '../types'

const HPCJobDetailPage = observer(() => {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'overview' | 'metrics' | 'checkpoints' | 'artifacts'>('overview')
  const [parameterEditing, setParameterEditing] = useState(false)
  const [newParams, setNewParams] = useState<Record<string, any>>({})

  useEffect(() => {
    if (jobId) {
      hpcStore.fetchJob(jobId)
      hpcStore.fetchCheckpoints(jobId)
      hpcStore.fetchArtifacts(jobId)
    }

    return () => {
      hpcStore.clearSelectedJob()
    }
  }, [jobId])

  // Polling for metrics when job is running
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null

    if (jobId && hpcStore.selectedJob?.status === HPCJobStatus.RUNNING) {
      interval = setInterval(() => {
        hpcStore.fetchJobMetrics(jobId)
      }, 30000) // Every 30 seconds
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [jobId, hpcStore.selectedJob?.status])

  const job = hpcStore.selectedJob

  if (hpcStore.jobLoading || !job) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Loading job details...</p>
      </div>
    )
  }

  const getStatusBadgeVariant = (status: HPCJobStatus): 'gray' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case HPCJobStatus.COMPLETED:
        return 'success'
      case HPCJobStatus.RUNNING:
      case HPCJobStatus.CHECKPOINTING:
        return 'warning'
      case HPCJobStatus.FAILED:
      case HPCJobStatus.CANCELLED:
        return 'error'
      default:
        return 'gray'
    }
  }

  const canPause = job.status === HPCJobStatus.RUNNING
  const canResume = job.status === HPCJobStatus.PAUSED
  const canCancel = ![HPCJobStatus.COMPLETED, HPCJobStatus.FAILED, HPCJobStatus.CANCELLED].includes(job.status)
  const canModifyParams = [HPCJobStatus.RUNNING, HPCJobStatus.PAUSED].includes(job.status)

  const handleSubmit = async () => {
    if (jobId) {
      await hpcStore.submitJob(jobId)
    }
  }

  const handlePause = async () => {
    if (jobId) {
      await hpcStore.pauseJob(jobId)
    }
  }

  const handleResume = async () => {
    if (jobId) {
      await hpcStore.resumeJob(jobId)
    }
  }

  const handleCancel = async () => {
    if (jobId && confirm('Are you sure you want to cancel this job?')) {
      await hpcStore.cancelJob(jobId)
    }
  }

  const handleTriggerCheckpoint = async () => {
    if (jobId) {
      const name = prompt('Checkpoint name (optional):')
      await hpcStore.triggerCheckpoint(jobId, name || undefined)
    }
  }

  const handleUpdateParameters = async () => {
    if (jobId && Object.keys(newParams).length > 0) {
      await hpcStore.updateJobParameters(jobId, newParams)
      setParameterEditing(false)
      setNewParams({})
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString()
  }

  const formatDuration = (startDate?: string, endDate?: string) => {
    if (!startDate) return '-'
    const start = new Date(startDate)
    const end = endDate ? new Date(endDate) : new Date()
    const diffMs = end.getTime() - start.getTime()
    const hours = Math.floor(diffMs / 3600000)
    const minutes = Math.floor((diffMs % 3600000) / 60000)
    return `${hours}h ${minutes}m`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/hpc')}
              className="text-muted-foreground hover:text-foreground"
            >
              &larr; Back
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">
              {job.job_name}
            </h1>
            <Badge variant={getStatusBadgeVariant(job.status)}>
              {job.status}
            </Badge>
          </div>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            {job.description || 'No description'}
          </p>
        </div>
        <div className="flex gap-2">
          {job.status === HPCJobStatus.DRAFT && (
            <Button onClick={handleSubmit}>Submit Job</Button>
          )}
          {canPause && (
            <Button variant="secondary" onClick={handlePause}>
              Pause
            </Button>
          )}
          {canResume && (
            <Button onClick={handleResume}>Resume</Button>
          )}
          {job.status === HPCJobStatus.RUNNING && (
            <Button variant="secondary" onClick={handleTriggerCheckpoint}>
              Checkpoint
            </Button>
          )}
          {canCancel && (
            <Button variant="danger" onClick={handleCancel}>
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      {job.status === HPCJobStatus.RUNNING || job.status === HPCJobStatus.CHECKPOINTING ? (
        <Card>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">
              Progress
            </span>
            <span className="text-sm font-medium text-foreground">
              {job.progress_percent.toFixed(1)}%
              {job.current_epoch !== undefined && job.total_epochs && (
                <span className="text-muted-foreground ml-2">
                  (Epoch {job.current_epoch}/{job.total_epochs})
                </span>
              )}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-3">
            <div
              className="bg-primary-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${job.progress_percent}%` }}
            />
          </div>
        </Card>
      ) : null}

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'metrics', 'checkpoints', 'artifacts'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as typeof activeTab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {tab}
              {tab === 'checkpoints' && ` (${hpcStore.checkpoints.length})`}
              {tab === 'artifacts' && ` (${hpcStore.artifacts.length})`}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Job Info */}
          <Card>
            <h3 className="text-lg font-medium mb-4">Job Information</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">PBS Job ID</dt>
                <dd className="font-mono">{job.pbs_job_id || '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Type</dt>
                <dd>{job.job_type}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Queue</dt>
                <dd>{job.queue}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Created</dt>
                <dd>{formatDate(job.created_at)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Submitted</dt>
                <dd>{formatDate(job.submitted_at)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Started</dt>
                <dd>{formatDate(job.started_at)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Duration</dt>
                <dd>{formatDuration(job.started_at, job.completed_at)}</dd>
              </div>
            </dl>
          </Card>

          {/* Resources */}
          <Card>
            <h3 className="text-lg font-medium mb-4">Resources</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Nodes</dt>
                <dd>{job.resources.nodes}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">CPUs per Node</dt>
                <dd>{job.resources.cpus_per_node}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">GPUs</dt>
                <dd>{job.resources.gpus} {job.resources.gpu_type && `(${job.resources.gpu_type})`}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Memory</dt>
                <dd>{job.resources.memory_gb} GB</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Walltime</dt>
                <dd>{Math.floor(job.resources.walltime_seconds / 3600)}h</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Node Hours Used</dt>
                <dd>{job.node_hours_used.toFixed(2)}</dd>
              </div>
            </dl>
          </Card>

          {/* Training Parameters */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Training Parameters</h3>
              {canModifyParams && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setParameterEditing(!parameterEditing)}
                >
                  {parameterEditing ? 'Cancel' : 'Edit'}
                </Button>
              )}
            </div>
            {parameterEditing ? (
              <div className="space-y-4">
                <textarea
                  className="w-full h-40 font-mono text-sm p-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={JSON.stringify(newParams, null, 2) || JSON.stringify(job.training_parameters, null, 2)}
                  onChange={(e) => {
                    try {
                      setNewParams(JSON.parse(e.target.value))
                    } catch { /* invalid JSON */ }
                  }}
                />
                <Button onClick={handleUpdateParameters}>
                  Update Parameters
                </Button>
              </div>
            ) : (
              <pre className="text-sm bg-muted/50 p-3 rounded-lg overflow-auto max-h-60">
                {JSON.stringify(job.training_parameters, null, 2)}
              </pre>
            )}
          </Card>

          {/* Checkpointing Config */}
          <Card>
            <h3 className="text-lg font-medium mb-4">Checkpointing</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Enabled</dt>
                <dd>{job.checkpoint_enabled ? 'Yes' : 'No'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Interval</dt>
                <dd>{job.checkpoint_interval_minutes} minutes</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Auto Resume</dt>
                <dd>{job.auto_resume ? 'Yes' : 'No'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Total Checkpoints</dt>
                <dd>{job.checkpoints_count}</dd>
              </div>
            </dl>
          </Card>
        </div>
      )}

      {/* Metrics Tab */}
      {activeTab === 'metrics' && (
        <Card>
          <h3 className="text-lg font-medium mb-4">Training Metrics</h3>
          {Object.keys(job.metrics).length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(job.metrics).map(([key, value]) => (
                <div key={key} className="bg-muted/50 rounded-lg p-4">
                  <div className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</div>
                  <div className="text-2xl font-bold text-foreground">
                    {typeof value === 'number' ? value.toFixed(4) : String(value)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No metrics available yet
            </div>
          )}

          {/* Resource Usage */}
          {Object.keys(job.resource_usage).length > 0 && (
            <div className="mt-6">
              <h4 className="text-md font-medium mb-3">Resource Usage</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(job.resource_usage).map(([key, value]) => (
                  <div key={key} className="bg-muted/50 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</div>
                    <div className="text-xl font-bold text-foreground">
                      {typeof value === 'number' ? value.toFixed(1) : String(value)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Checkpoints Tab */}
      {activeTab === 'checkpoints' && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Checkpoints</h3>
            {job.status === HPCJobStatus.RUNNING && (
              <Button size="sm" onClick={handleTriggerCheckpoint}>
                Create Checkpoint
              </Button>
            )}
          </div>
          {hpcStore.checkpointsLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading checkpoints...</div>
          ) : hpcStore.checkpoints.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No checkpoints yet
            </div>
          ) : (
            <div className="space-y-3">
              {hpcStore.checkpoints.map((checkpoint) => (
                <div
                  key={checkpoint.id}
                  className="border border-border rounded-lg p-4"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          #{checkpoint.checkpoint_number}
                        </span>
                        {checkpoint.checkpoint_name && (
                          <span className="text-muted-foreground">
                            ({checkpoint.checkpoint_name})
                          </span>
                        )}
                        <Badge variant={checkpoint.is_auto ? 'gray' : 'success'}>
                          {checkpoint.is_auto ? 'Auto' : 'Manual'}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Epoch {checkpoint.epoch || '-'} | Step {checkpoint.step || '-'} |
                        Size: {checkpoint.size_mb.toFixed(2)} MB
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {checkpoint.is_valid && job.status !== HPCJobStatus.COMPLETED && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => hpcStore.resumeFromCheckpoint(jobId!, checkpoint.id)}
                        >
                          Resume From
                        </Button>
                      )}
                      {checkpoint.downloaded ? (
                        <Badge variant="success">Downloaded</Badge>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Artifacts Tab */}
      {activeTab === 'artifacts' && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Artifacts</h3>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => hpcStore.scanArtifacts(jobId!)}
            >
              Scan for Artifacts
            </Button>
          </div>
          {hpcStore.artifactsLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading artifacts...</div>
          ) : hpcStore.artifacts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No artifacts found. Click "Scan for Artifacts" to discover outputs.
            </div>
          ) : (
            <div className="space-y-3">
              {hpcStore.artifacts.map((artifact) => (
                <div
                  key={artifact.id}
                  className="border border-border rounded-lg p-4"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{artifact.name}</span>
                        <Badge variant="gray">{artifact.artifact_type}</Badge>
                        {artifact.is_expired && (
                          <Badge variant="error">Expired</Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1 font-mono">
                        {artifact.remote_path}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Size: {artifact.size_human} |
                        Downloads: {artifact.download_count}
                      </div>
                    </div>
                    <div>
                      {artifact.downloaded ? (
                        <Badge variant="success">Downloaded</Badge>
                      ) : !artifact.is_expired ? (
                        <Button
                          size="sm"
                          onClick={() => hpcStore.downloadArtifact(artifact.id)}
                        >
                          Download
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Error Display */}
      {job.error_message && (
        <Card className="border-red-500/20 bg-red-500/10">
          <h3 className="text-lg font-medium text-red-600 mb-2">
            Error
          </h3>
          <pre className="text-sm text-red-600 whitespace-pre-wrap">
            {job.error_message}
          </pre>
        </Card>
      )}
    </div>
  )
})

export default HPCJobDetailPage
